import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import api, { setOn401Callback } from '../services/api';
import SessionLoginModal from '../components/auth/SessionLoginModal';

import { User } from '../types/auth';
import { decryptPrivateKey } from '../utils/cryptoUtil';

interface AuthContextType {
    user: User | null;
    token: string | null;
    loginLogId: string | null;
    privateKey: CryptoKey | null;
    signingPrivateKey: CryptoKey | null;
    setPrivateKey: (key: CryptoKey | null) => void;
    setSigningPrivateKey: (key: CryptoKey | null) => void;
    login: (email: string, password: string) => Promise<any>;
    register: (userData: any) => Promise<void>;
    logout: () => void;
    loading: boolean;
    updateUser: (userData: User) => void;
    refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [loginLogId, setLoginLogId] = useState<string | null>(localStorage.getItem('loginLogId'));
    const [privateKey, setPrivateKey] = useState<CryptoKey | null>(null);
    const [signingPrivateKey, setSigningPrivateKey] = useState<CryptoKey | null>(null);
    const [loading, setLoading] = useState(true);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const isInitialMount = useRef(true);

    const logout = useCallback(async () => {
        // Record logout on server
        const storedLoginLogId = localStorage.getItem('loginLogId');
        if (storedLoginLogId) {
            try {
                await api.post('/api/auth/logout', { loginLogId: storedLoginLogId });
            } catch (error) {
                console.error('Error recording logout', error);
            }
        }

        localStorage.removeItem('token');
        localStorage.removeItem('loginLogId');
        setToken(null);
        setLoginLogId(null);
        setUser(null);
        setPrivateKey(null);
        setSigningPrivateKey(null);
    }, []);

    useEffect(() => {
        // Register 401 callback
        setOn401Callback(() => {
            if (token && window.location.pathname !== '/login') {
                setIsSessionExpired(true);
            }
        });

        const fetchUser = async () => {
            if (token) {
                try {
                    const res = await api.get('/api/auth/me');
                    setUser(res.data);
                } catch (error) {
                    console.error('Error fetching user', error);
                    // Only logout if not initial mount to prevent double calls
                    if (!isInitialMount.current) {
                        logout();
                    }
                }
            } else {
                setUser(null);
            }
            setLoading(false);
            isInitialMount.current = false;
        };

        fetchUser();
    }, [token, logout]);

    const login = async (email: string, password: string) => {
        const res = await api.post('/api/auth/login', { email, password });
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);

        // Optimize: Set user immediately to prevent "double login" race condition
        // The payload from login is partial but contains role needed for PrivateRoute
        // The useEffect will fetch the full user details in the background
        const userData = { ...res.data.user, _id: res.data.user.id } as User;
        setUser(userData);

        if (userData.encryptedPrivateKey && password) {
            try {
                const key = await decryptPrivateKey(userData.encryptedPrivateKey, password, 'RSA-OAEP');
                setPrivateKey(key);
                
                // Also decrypt signing key for Pillar 4
                if (userData.encryptedSigningPrivateKey) {
                    const sigKey = await decryptPrivateKey(userData.encryptedSigningPrivateKey, password, 'RSA-PSS');
                    setSigningPrivateKey(sigKey);
                } else {
                    // Auto-migration: if user has encryption keys but NO signing keys, generate them
                    console.info("Auto-migrating: Generating signing keys for user...");
                    const { generateSigningKeyPair, exportPublicKey, encryptPrivateKey } = await import('../utils/cryptoUtil');
                    const sigPair = await generateSigningKeyPair();
                    const encSigPriv = await encryptPrivateKey(sigPair.privateKey, password);
                    const sigPub = await exportPublicKey(sigPair.publicKey);
                    
                    await api.patch(`/api/users/${userData._id}`, {
                        signingPublicKey: sigPub,
                        encryptedSigningPrivateKey: encSigPriv
                    });
                    
                    // Decrypt newly generated key to store in RAM (non-extractable)
                    const sigKey = await decryptPrivateKey(encSigPriv, password, 'RSA-PSS');
                    setSigningPrivateKey(sigKey);
                }
            } catch (err) {
                console.error("Failed to decrypt or migrate keys", err);
            }
        }

        if (res.data.loginLogId) {
            localStorage.setItem('loginLogId', res.data.loginLogId);
            setLoginLogId(res.data.loginLogId);
        }
        return res.data;
    };

    const sessionLogin = async (email: string, pass: string) => {
        const res = await api.post('/api/auth/login', { email, password: pass });
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        setIsSessionExpired(false);
        
        const userData = res.data.user;
        if (userData?.encryptedPrivateKey) {
            try {
                const key = await decryptPrivateKey(userData.encryptedPrivateKey, pass, 'RSA-OAEP');
                setPrivateKey(key);
                if (userData.encryptedSigningPrivateKey) {
                    const sigKey = await decryptPrivateKey(userData.encryptedSigningPrivateKey, pass, 'RSA-PSS');
                    setSigningPrivateKey(sigKey);
                }
            } catch (err) {
                console.error("Failed to decrypt keys in sessionLogin", err);
            }
        }
        
        await refreshUser();
    };

    const register = async (userData: any) => {
        const res = await api.post('/api/auth/register', userData);
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
    };

    const updateUser = (userData: User) => {
        setUser(userData);
    };

    const refreshUser = async () => {
        if (token) {
            try {
                const res = await api.get('/api/auth/me');
                setUser(res.data);
            } catch (error) {
                console.error('Error refreshing user', error);
            }
        }
    };

    return (
        <AuthContext.Provider value={{ 
            user, token, loginLogId, privateKey, signingPrivateKey, 
            setPrivateKey, setSigningPrivateKey, login, logout, register, 
            loading, updateUser, refreshUser 
        }}>
            {children}
            <SessionLoginModal
                open={isSessionExpired}
                onLogin={sessionLogin}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
